import React from "react";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram } from "lucide-react";
import Embers from "./Embers";
import { SITE } from "../lib/data";
import SectionNumber from "./SectionNumber";

export default function Contact() {
  return (
    <section
      id="kapcsolat"
      data-testid="contact-section"
      className="relative bg-white overflow-hidden"
    >
      {/* Dark CTA block */}
      <div className="relative bg-pkcharcoal overflow-hidden">
        <SectionNumber n="09" variant="dark" />
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 50% 100%, rgba(217,4,41,0.5) 0%, transparent 60%)",
          }}
        />
        <Embers count={14} />
        <div className="grain opacity-10" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-24 sm:py-32 text-center">
          <span
            className="eyebrow center"
            style={{ color: "#FFA64D", justifyContent: "center" }}
          >
            Foglalás
          </span>
          <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-7xl mt-6 leading-[1.05]">
            Gyere el<span className="text-pkred">,</span>
            <br />
            ülj le <span className="italic">a tűz mellé.</span>
          </h2>
          <p className="mt-8 text-white/70 text-base sm:text-lg max-w-xl mx-auto">
            A foglalás telefonon vagy emailben történik. Hívj minket, vagy
            küldj egy üzenetet – hamar válaszolunk.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={SITE.phoneHref}
              data-testid="contact-cta-phone"
              className="btn-fire"
            >
              <Phone className="w-4 h-4" strokeWidth={1.8} />
              Asztalfoglalás
            </a>
            <a
              href={SITE.emailHref}
              data-testid="contact-cta-email"
              className="btn-ghost"
            >
              <Mail className="w-4 h-4" strokeWidth={1.8} />
              Vendégház foglalás
            </a>
            <a
              href={SITE.phoneHref}
              data-testid="contact-cta-program"
              className="btn-ghost"
            >
              Programra jelentkezem
            </a>
          </div>
        </div>
      </div>

      {/* Info + map */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 reveal">
          <h3 className="font-serif text-pkcharcoal text-3xl sm:text-4xl leading-tight">
            Találj ránk Vezsenyen<span className="text-pkred">.</span>
          </h3>
          <div className="mt-10 space-y-7">
            <InfoRow icon={MapPin} title="Cím">
              {SITE.address}
            </InfoRow>
            <InfoRow icon={Phone} title="Telefon">
              <a
                href={SITE.phoneHref}
                data-testid="info-phone"
                className="link-fire"
              >
                {SITE.phone}
              </a>
            </InfoRow>
            <InfoRow icon={Mail} title="Email">
              <a
                href={SITE.emailHref}
                data-testid="info-email"
                className="link-fire"
              >
                {SITE.email}
              </a>
            </InfoRow>
            <InfoRow icon={Clock} title="Nyitvatartás">
              Foglalással · péntek – vasárnap
              <br />
              <span className="text-pkcharcoal/50 text-sm">
                Tematikus estek előzetes egyeztetéssel
              </span>
            </InfoRow>
          </div>

          <div className="mt-12 flex items-center gap-3">
            <a
              href={SITE.social.facebook}
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              data-testid="social-facebook"
              className="w-10 h-10 rounded-full border border-pkash flex items-center justify-center text-pkcharcoal hover:bg-pkred hover:text-white hover:border-pkred transition-colors"
            >
              <Facebook className="w-4 h-4" strokeWidth={1.6} />
            </a>
            <a
              href={SITE.social.instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              data-testid="social-instagram"
              className="w-10 h-10 rounded-full border border-pkash flex items-center justify-center text-pkcharcoal hover:bg-pkred hover:text-white hover:border-pkred transition-colors"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.6} />
            </a>
          </div>
        </div>

        <div className="lg:col-span-7 reveal">
          <div className="aspect-[4/3] lg:aspect-[5/4] w-full overflow-hidden border border-pkash">
            <iframe
              title="Pakkaland térkép"
              data-testid="contact-map"
              src={SITE.mapEmbed}
              width="100%"
              height="100%"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full grayscale-[30%]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, title, children }) {
  return (
    <div className="flex items-start gap-5">
      <div className="w-11 h-11 shrink-0 rounded-full bg-pkbeige flex items-center justify-center">
        <Icon className="w-4 h-4 text-pkred" strokeWidth={1.8} />
      </div>
      <div>
        <div className="text-[0.65rem] tracking-[0.3em] uppercase text-pkcharcoal/50 font-semibold">
          {title}
        </div>
        <div className="text-pkcharcoal text-base sm:text-lg mt-1">
          {children}
        </div>
      </div>
    </div>
  );
}
